import { useCallback, useEffect, useMemo, useState } from "react";
import { Pencil, Phone, Plus, Search, Trash2, UserRound } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LivreurFiche, RoleBadge, ScanChip, TourneeTag } from "@/components/livreurs/LivreurFiche";
import { LivreurForm } from "@/components/livreurs/LivreurForm";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { peut } from "@/lib/permissions";

const ALL = "__all__";

export default function LivreursPage() {
  const { user } = useAuth();
  const peutCreer = peut(user, "chauffeurs_creer");
  const peutModifier = peut(user, "chauffeurs_modifier");
  const peutSupprimer = peut(user, "chauffeurs_supprimer");
  const [livreurs, setLivreurs] = useState([]);
  const [tournees, setTournees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState(ALL);
  const [sstFilter, setSstFilter] = useState(ALL);
  const [form, setForm] = useState(null); // {livreur?}
  const [fiche, setFiche] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    try {
      const [l, t] = await Promise.all([api.get("/livreurs"), api.get("/tournees")]);
      setLivreurs(l.data);
      setTournees(t.data.tournees);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const tByCode = useMemo(() => Object.fromEntries(tournees.map((t) => [t.code, t])), [tournees]);
  const ssts = useMemo(
    () => [...new Set([...tournees.map((t) => t.sst), ...livreurs.map((l) => l.sst)].filter(Boolean))].sort(),
    [tournees, livreurs],
  );

  const shown = livreurs.filter((l) => {
    if (roleFilter !== ALL && l.role !== roleFilter) return false;
    if (sstFilter !== ALL && l.sst !== sstFilter) return false;
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [l.nom, l.prenom, l.telephone, l.sst, ...(l.tournees || [])].some((v) => (v || "").toLowerCase().includes(s))
      || (l.telephone || "").replace(/\./g, "").includes(s.replace(/\D/g, "") || "§");
  });

  const save = async (data) => {
    const payload = { ...data };
    try {
      if (form.livreur?.id) {
        await api.put(`/livreurs/${form.livreur.id}`, payload);
        toast.success("Fiche mise à jour");
      } else {
        await api.post("/livreurs", payload);
        toast.success(`${payload.prenom} ${payload.nom} ajouté à la liste`);
      }
      setForm(null);
      load();
    } catch (e) {
      throw new Error(errMsg(e));
    }
  };

  const del = async (l) => {
    try {
      await api.delete(`/livreurs/${l.id}`);
      toast.success("Fiche supprimée");
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const nbPrincipaux = livreurs.filter((l) => l.role === "principal").length;

  return (
    <AppShell title="Fiches chauffeurs">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Fiches chauffeurs</h1>
          <p className="text-sm text-muted-foreground">
            {livreurs.length} livreur(s) · {nbPrincipaux} principal(aux) · {livreurs.length - nbPrincipaux} secondaire(s)
            {!peutCreer && !peutModifier && !peutSupprimer && " — consultation seule"}
          </p>
        </div>
        {peutCreer && (
          <Button onClick={() => setForm({ livreur: null })} data-testid="btn-creer-fiche">
            <Plus size={15} /> Créer fiche
          </Button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative sm:w-80">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, téléphone, tournée…" className="pl-9" data-testid="livreurs-search" />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="sm:w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Tous les chauffeurs</SelectItem>
            <SelectItem value="principal">Principaux</SelectItem>
            <SelectItem value="secondaire">Secondaires</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sstFilter} onValueChange={setSstFilter}>
          <SelectTrigger className="sm:w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Toutes les SST</SelectItem>
            {ssts.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Chargement…</p>
      ) : shown.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground">
          <UserRound className="mx-auto mb-2 opacity-60" />
          {livreurs.length === 0
            ? (peutCreer ? "Aucune fiche pour l'instant. Cliquez sur « Créer fiche »." : "Aucune fiche livreur pour l'instant.")
            : "Aucun livreur ne correspond à la recherche."}
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="livreurs-list">
          {shown.map((l) => (
            <div
              key={l.id}
              className="rounded-xl border border-border bg-card p-4 hover:border-primary/50 transition-colors cursor-pointer"
              onClick={() => setFiche(l)}
              data-testid={`livreur-card-${l.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0">
                  <UserRound size={19} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">{l.prenom} {l.nom}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <RoleBadge role={l.role} />
                    {l.sst && <span className="text-xs text-muted-foreground truncate">{l.sst}</span>}
                  </div>
                </div>
                {(peutModifier || peutSupprimer) && (
                  <div className="flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                    {peutModifier && <Button size="sm" variant="ghost" onClick={() => setForm({ livreur: l })} title="Modifier"><Pencil size={14} /></Button>}
                    {peutSupprimer && (confirmDel === l.id ? (
                      <Button size="sm" variant="destructive" onClick={() => del(l)}>Supprimer ?</Button>
                    ) : (
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setConfirmDel(l.id)} title="Supprimer"><Trash2 size={14} /></Button>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-3 flex items-center justify-between gap-2">
                <div className="flex flex-wrap gap-1">
                  {(l.tournees || []).map((c) => <TourneeTag key={c} code={c} tournee={tByCode[c]} defaut={c === l.tournee_defaut} />)}
                  {!l.tournees?.length && <span className="text-xs text-muted-foreground">Aucune tournée</span>}
                  {(l.scans_en_cours || []).map((sc) => <ScanChip key={sc.attribution_id} scan={sc} showTournee />)}
                </div>
                {l.telephone && (
                  <a href={`tel:${l.telephone.replace(/\./g, "")}`} onClick={(e) => e.stopPropagation()} className="font-mono text-xs inline-flex items-center gap-1 text-muted-foreground hover:text-foreground shrink-0">
                    <Phone size={12} /> {l.telephone}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <LivreurFiche
        livreur={fiche}
        tourneesByCode={tByCode}
        open={!!fiche}
        onClose={() => setFiche(null)}
        footer={peutModifier && fiche ? (
          <div className="border-t border-border pt-3 flex justify-end">
            <Button size="sm" variant="outline" onClick={() => { setForm({ livreur: fiche }); setFiche(null); }}><Pencil size={13} /> Modifier la fiche</Button>
          </div>
        ) : null}
      />

      {(peutCreer || peutModifier) && (
        <LivreurForm
          open={!!form}
          livreur={form?.livreur}
          tournees={tournees}
          ssts={ssts}
          onClose={() => setForm(null)}
          onSave={save}
        />
      )}
    </AppShell>
  );
}
